import { useState, useEffect, useRef } from 'react';
import { createItem, updateItem, getItems } from '../../api/figApi';
import { FAMILY_LISTS } from '../../api/figApi';
import { getSignedGetUrl, uploadCover } from '../api/b2';
import { extractAudioMetadata, extractPhotoMetadata, extractVideoMetadata } from '../api/metadataExtraction';
import { resolveB2Url } from '../api/storageResolver';

const MEDIA_TYPES = ['Audiobook', 'Music', 'Movie', 'Video'];

function b2Uri(key) {
  return `b2://${process.env.REACT_APP_B2_BUCKET_NAME || 'familyvault-media-fragos'}/${key}`;
}

export default function ClaimModal({ orphan, onClose, onClaimed, getToken, mode = 'claim', existingItem = null }) {
  const isEdit = mode === 'edit';

  if (mode === 'edit' && !existingItem) {
    throw new Error('ClaimModal: existingItem required when mode is edit');
  }

  const [mediaType, setMediaType]       = useState(isEdit ? existingItem.MediaType              : (orphan?.mediaTypeGuess || ''));
  const [title, setTitle]               = useState(isEdit ? existingItem.Title                  : titleFromKey(orphan?.key || ''));
  const [fields, setFields]             = useState(isEdit ? reverseMapItemToFields(existingItem) : parseInitialClaimFields(orphan?.key));
  const [coverFile, setCoverFile]       = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [extracting, setExtracting]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState(null);
  const [existingBookTitles, setExistingBookTitles] = useState([]);
  const coverInputRef = useRef();

  const initialFieldsRef    = useRef(isEdit ? reverseMapItemToFields(existingItem) : null);
  const initialTitleRef     = useRef(isEdit ? existingItem?.Title                  : null);
  const initialMediaTypeRef = useRef(isEdit ? existingItem?.MediaType              : null);

  useEffect(() => {
    if (!isEdit || !existingItem?.CoverImageUrl) return;
    let cancelled = false;
    resolveB2Url(existingItem.CoverImageUrl)
      .then(url => { if (!cancelled) setCoverPreview(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEdit) return;
    let cancelled = false;
    async function fetchBookTitles() {
      try {
        const token = await getToken();
        const items = await getItems(token, FAMILY_LISTS.MEDIA_ITEMS);
        if (cancelled) return;
        const titles = [...new Set(
          items.filter(i => i.BookTitle).map(i => i.BookTitle)
        )].sort();
        setExistingBookTitles(titles);
      } catch {
        // non-blocking
      }
    }
    fetchBookTitles();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleField(name, value) {
    setFields(f => ({ ...f, [name]: value }));
  }

  async function handleExtract() {
    if (!mediaType) return;
    setExtracting(true);
    setError(null);
    try {
      const url = await getSignedGetUrl(orphan.key);
      let extracted = {};
      if (mediaType === 'Audiobook' || mediaType === 'Music') {
        extracted = await extractAudioMetadata(url);
      } else if (mediaType === 'Photo') {
        extracted = await extractPhotoMetadata(url);
      } else {
        extracted = await extractVideoMetadata(url);
      }
      if (extracted.title && !title) setTitle(extracted.title);
      setFields(f => ({ ...f, ...removeNulls(extracted) }));
    } catch (err) {
      setError(`Extraction failed: ${err.message}`);
    } finally {
      setExtracting(false);
    }
  }

  function handleCoverChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    if (!title.trim() || !mediaType) {
      setError('Title and Media Type are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();

      let coverImageUrl = null;
      if (coverFile) {
        const mediaKey = isEdit
          ? existingItem.StorageLocation.replace(/^b2:\/\/[^/]+\//, '')
          : orphan.key;
        const coverKey = await uploadCover(mediaKey, coverFile);
        coverImageUrl = b2Uri(coverKey);
      }

      if (isEdit) {
        const patch = {};
        const trimmedTitle = title.trim();
        if (trimmedTitle !== initialTitleRef.current) patch.Title = trimmedTitle;
        if (mediaType !== initialMediaTypeRef.current) patch.MediaType = mediaType;

        const currentColumns = mapExtractedToColumns(fields, mediaType);
        const initialColumns = mapExtractedToColumns(initialFieldsRef.current, mediaType);
        const allKeys = new Set([...Object.keys(currentColumns), ...Object.keys(initialColumns)]);
        for (const key of allKeys) {
          if (currentColumns[key] !== initialColumns[key]) {
            patch[key] = currentColumns[key] ?? null;
          }
        }

        if (coverImageUrl) patch.CoverImageUrl = coverImageUrl;

        if (Object.keys(patch).length === 0) {
          setSaving(false);
          onClose();
          return;
        }

        const updated = await updateItem(token, FAMILY_LISTS.MEDIA_ITEMS, existingItem.id, patch);
        onClaimed(updated);
      } else {
        const created = await createItem(token, FAMILY_LISTS.MEDIA_ITEMS, {
          Title:           title.trim(),
          MediaType:       mediaType,
          StorageLocation: b2Uri(orphan.key),
          FileFormat:      orphan.key.split('.').pop().toLowerCase(),
          ...(coverImageUrl ? { CoverImageUrl: coverImageUrl } : {}),
          ...mapExtractedToColumns(fields, mediaType),
        });
        onClaimed(created);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="FV-modal-overlay" onClick={onClose}>
      <div className="FV-modal" onClick={e => e.stopPropagation()}>
        <div className="FV-modal-header">
          <h2 className="FV-modal-title">{isEdit ? 'Edit metadata' : 'Claim File'}</h2>
          <button className="FV-modal-close" onClick={onClose}>&#x2715;</button>
        </div>

        {!isEdit && <p className="FV-modal-key">{orphan?.key}</p>}

        {error && <p className="FV-modal-error">{error}</p>}

        <div className="FV-modal-row">
          <label className="FV-modal-label">Media Type</label>
          <select
            className="FV-modal-select"
            value={mediaType}
            onChange={e => setMediaType(e.target.value)}
          >
            <option value="">— select —</option>
            {MEDIA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="FV-modal-row">
          <label className="FV-modal-label">Title</label>
          <input
            className="FV-modal-input"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>

        {!isEdit && (
          <button
            className="FV-modal-btn FV-modal-btn--secondary"
            onClick={handleExtract}
            disabled={!mediaType || extracting}
          >
            {extracting ? 'Extracting…' : 'Extract Metadata'}
          </button>
        )}

        {mediaType && <MetadataFields mediaType={mediaType} fields={fields} onChange={handleField} bookTitles={existingBookTitles} />}

        <div className="FV-modal-row FV-modal-row--cover">
          <label className="FV-modal-label">Cover Image</label>
          {coverPreview && <img className="FV-modal-cover-preview" src={coverPreview} alt="cover preview" />}
          <button className="FV-modal-btn FV-modal-btn--secondary" onClick={() => coverInputRef.current.click()}>
            {coverFile ? 'Change Cover' : (isEdit && coverPreview ? 'Replace Cover' : 'Choose Cover')}
          </button>
          <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleCoverChange} />
        </div>

        <div className="FV-modal-actions">
          <button className="FV-modal-btn FV-modal-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Claim')}
          </button>
          <button className="FV-modal-btn FV-modal-btn--ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function MetadataFields({ mediaType, fields, onChange, bookTitles = [] }) {
  const f = (name, label, type = 'text') => (
    <div className="FV-modal-row" key={name}>
      <label className="FV-modal-label">{label}</label>
      <input
        className="FV-modal-input"
        type={type}
        value={fields[name] ?? ''}
        onChange={e => onChange(name, type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
      />
    </div>
  );

  if (mediaType === 'Audiobook') return (
    <>
      <div className="FV-modal-row">
        <label className="FV-modal-label">Book</label>
        <input
          className="FV-modal-input"
          type="text"
          list="fv-book-titles"
          value={fields.bookTitle ?? ''}
          onChange={e => onChange('bookTitle', e.target.value)}
        />
        <datalist id="fv-book-titles">
          {bookTitles.map(t => <option key={t} value={t} />)}
        </datalist>
      </div>
      {f('chapterNumber', 'Chapter', 'number')}
      {f('artist', 'Author')}
      {f('narrator', 'Narrator')}
      {f('album', 'Series')}
      {f('year', 'Year', 'number')}
      {f('duration', 'Duration (sec)', 'number')}
    </>
  );

  if (mediaType === 'Music') return (
    <>
      {f('artist', 'Artist')}
      {f('album', 'Album')}
      {f('trackNumber', 'Track #', 'number')}
      {f('year', 'Year', 'number')}
      {f('composer', 'Composer')}
      {f('duration', 'Duration (sec)', 'number')}
    </>
  );

  if (mediaType === 'Movie' || mediaType === 'Video') return (
    <>
      {f('duration', 'Duration (sec)', 'number')}
      {f('year', 'Year', 'number')}
    </>
  );

  if (mediaType === 'Photo') return (
    <>
      {f('dateOfEvent', 'Date of Event')}
      {f('location', 'Location')}
      {f('people', 'People (comma-sep)')}
    </>
  );

  return null;
}

function titleFromKey(key) {
  const filename = key.split('/').pop().replace(/\.[^.]+$/, '');
  let m;
  m = /^Chapter[_\s-]?(\d+)[_\s-]+(.+)$/i.exec(filename);
  if (m) {
    const num = parseInt(m[1], 10);
    const text = m[2].replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
    return text || `Chapter ${String(num).padStart(2, '0')}`;
  }
  m = /^Chapter[_\s-]?(\d+)$/i.exec(filename);
  if (m) {
    const num = parseInt(m[1], 10);
    return `Chapter ${String(num).padStart(2, '0')}`;
  }
  return filename.replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
}

function parseInitialClaimFields(key) {
  const filename = (key || '').split('/').pop().replace(/\.[^.]+$/, '');
  const m = /Chapter[_\s-]?(\d+)/i.exec(filename);
  return m ? { chapterNumber: parseInt(m[1], 10) } : {};
}

function removeNulls(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== ''));
}

function mapExtractedToColumns(fields, mediaType) {
  const map = {};
  if (fields.artist) {
    if (mediaType === 'Audiobook') map.BookAuthor = fields.artist;
    else map.Artist = fields.artist;
  }
  if (fields.narrator) map.Narrator = fields.narrator;
  if (fields.album) {
    if (mediaType === 'Audiobook') map.SeriesName = fields.album;
    else map.Album = fields.album;
  }
  if (fields.trackNumber) map.TrackNumber = fields.trackNumber;
  if (fields.year) {
    if (mediaType === 'Music') map.RecordingYear = fields.year;
    else if (mediaType === 'Movie') map.ReleaseYear = fields.year;
    // Audiobook, Video, Photo: no year column in schema — no-op
  }
  if (fields.composer) map.Composer = fields.composer;
  if (fields.duration) map.Duration = fields.duration;
  if (fields.bookTitle)     map.BookTitle     = fields.bookTitle;
  if (fields.chapterNumber) map.ChapterNumber = Number(fields.chapterNumber);
  if (fields.dateOfEvent) map.DateOfEvent = fields.dateOfEvent;
  if (fields.location) map.Location = fields.location;
  if (fields.people) map.People = fields.people;
  return map;
}

// Reverse map: SharePoint columns → internal field names for
// MetadataFields. The `||` collapses (Artist || BookAuthor,
// Album || SeriesName, RecordingYear || ReleaseYear) are safe
// because MediaType is mutually exclusive — only one column is
// ever populated on a given record. Forward-map
// (mapExtractedToColumns) routes by mediaType; this side is
// type-agnostic by invariant. A future schema change that
// populated both columns on one record would break this
// assumption — guard accordingly if that ever happens.
function reverseMapItemToFields(item) {
  return removeNulls({
    artist:      item.Artist        || item.BookAuthor  || null,
    narrator:    item.Narrator      || null,
    album:       item.Album         || item.SeriesName  || null,
    trackNumber: item.TrackNumber   || null,
    year:        item.RecordingYear || item.ReleaseYear || null,
    composer:    item.Composer      || null,
    duration:    item.Duration      || null,
    dateOfEvent: item.DateOfEvent   || null,
    location:    item.Location      || null,
    people:      item.People        || null,
    bookTitle:     item.BookTitle     || null,
    chapterNumber: item.ChapterNumber || null,
  });
}
